import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { sendFissalChat } from '../services/api';
import { triggerHaptic } from '../services/purrSynthesizer';
import { ContextBreadcrumb } from '../components/ContextBreadcrumb';
import { Lightbulb, Sun, Moon, Sparkles, Power, Sliders } from 'lucide-react';

export const FamilyLightsView: React.FC = () => {
  const { profile } = useApp();

  const [lights, setLights] = useState([
    { id: '1', name: 'Living Room Ceiling', room: 'Living Room', isOn: true, color: '#fbbf24' },
    { id: '2', name: 'Cozy Lamp', room: 'Living Room Corner', isOn: true, color: '#f59e0b' },
    { id: '3', name: 'Kitchen Bar', room: 'Kitchen', isOn: false, color: '#ffffff' },
    { id: '4', name: 'Porch Light', room: 'Front Porch', isOn: true, color: '#38bdf8' },
  ]);

  const [activeScene, setActiveScene] = useState<string>('cozy');

  const toggleLight = (id: string) => {
    triggerHaptic(25);
    setLights((prev) => {
      const updated = prev.map((l) => (l.id === id ? { ...l, isOn: !l.isOn } : l));
      const target = updated.find((l) => l.id === id);
      if (target) {
        sendFissalChat(`Turn ${target.isOn ? 'on' : 'off'} the ${target.name}`, [], profile).catch(() => {});
      }
      return updated;
    });
  };

  const setScene = (sceneName: string) => {
    setActiveScene(sceneName);
    triggerHaptic([30, 40, 30]);

    if (sceneName === 'all_off') {
      setLights((prev) => prev.map((l) => ({ ...l, isOn: false })));
      sendFissalChat('Set lights scene to all_off', [], profile).catch(() => {});
    } else if (sceneName === 'cozy') {
      setLights((prev) =>
        prev.map((l) => ({ ...l, isOn: l.id === '2' || l.id === '1' }))
      );
      sendFissalChat('Set lights scene to cozy', [], profile).catch(() => {});
    } else if (sceneName === 'bright') {
      setLights((prev) => prev.map((l) => ({ ...l, isOn: true })));
      sendFissalChat('Set lights scene to bright', [], profile).catch(() => {});
    } else if (sceneName === 'evening') {
      setLights((prev) =>
        prev.map((l) => ({ ...l, isOn: l.id === '2' || l.id === '4' }))
      );
      sendFissalChat('Set lights scene to evening', [], profile).catch(() => {});
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-3.5 py-3 space-y-4">
      <ContextBreadcrumb realmName="Family Realm" deckTitle="Smart Lights" realmEmoji="💡" />
      {/* Header Card */}
      <div className="rounded-2xl p-4 bg-gradient-to-br from-amber-950/40 via-slate-900/80 to-slate-950 border border-amber-500/30 shadow-xl">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-400/40 flex items-center justify-center text-amber-300">
              <Lightbulb className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-cinzel text-sm font-bold text-amber-300 tracking-wider">
                CASTLE ECHO LIGHTS
              </h2>
              <p className="text-[11px] text-slate-300">
                Tuya Smart Household Ambience
              </p>
            </div>
          </div>
          <span className="text-[10px] font-mono text-amber-400 bg-amber-500/15 px-2.5 py-1 rounded-full border border-amber-500/30 font-semibold">
            {lights.filter((l) => l.isOn).length} Active
          </span>
        </div>

        <p className="text-xs text-slate-300 leading-relaxed mt-1">
          Adjust lighting across the living room, kitchen, and porch with one-tap scenes or individual controls.
        </p>

        {/* Scene Presets */}
        <div className="grid grid-cols-4 gap-2 pt-3">
          <button
            onClick={() => setScene('cozy')}
            className={`py-2 px-2 rounded-xl text-xs font-semibold flex flex-col items-center gap-1 border transition-all ${
              activeScene === 'cozy'
                ? 'bg-amber-500/30 border-amber-400 text-amber-200 shadow-quantum-glow'
                : 'bg-white/5 border-white/10 text-slate-300 hover:text-white'
            }`}
          >
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-[10px]">Cozy</span>
          </button>

          <button
            onClick={() => setScene('bright')}
            className={`py-2 px-2 rounded-xl text-xs font-semibold flex flex-col items-center gap-1 border transition-all ${
              activeScene === 'bright'
                ? 'bg-amber-500/30 border-amber-400 text-amber-200 shadow-quantum-glow'
                : 'bg-white/5 border-white/10 text-slate-300 hover:text-white'
            }`}
          >
            <Sun className="w-4 h-4 text-amber-300" />
            <span className="text-[10px]">Bright</span>
          </button>

          <button
            onClick={() => setScene('evening')}
            className={`py-2 px-2 rounded-xl text-xs font-semibold flex flex-col items-center gap-1 border transition-all ${
              activeScene === 'evening'
                ? 'bg-amber-500/30 border-amber-400 text-amber-200 shadow-quantum-glow'
                : 'bg-white/5 border-white/10 text-slate-300 hover:text-white'
            }`}
          >
            <Moon className="w-4 h-4 text-sky-400" />
            <span className="text-[10px]">Evening</span>
          </button>

          <button
            onClick={() => setScene('all_off')}
            className={`py-2 px-2 rounded-xl text-xs font-semibold flex flex-col items-center gap-1 border transition-all ${
              activeScene === 'all_off'
                ? 'bg-rose-500/30 border-rose-400 text-rose-200'
                : 'bg-white/5 border-white/10 text-slate-300 hover:text-white'
            }`}
          >
            <Power className="w-4 h-4 text-rose-400" />
            <span className="text-[10px]">All Off</span>
          </button>
        </div>
      </div>

      {/* Individual Light Cards */}
      <div className="glass-panel rounded-2xl p-3.5 border border-white/10 space-y-3">
        <h3 className="font-semibold text-xs text-white flex items-center gap-1.5">
          <Sliders className="w-4 h-4 text-amber-400" /> Room Fixtures
        </h3>

        <div className="grid grid-cols-2 gap-2.5">
          {lights.map((l) => (
            <button
              key={l.id}
              onClick={() => toggleLight(l.id)}
              className={`p-3.5 rounded-2xl border text-left transition-all relative overflow-hidden ${
                l.isOn
                  ? 'bg-gradient-to-br from-amber-500/20 to-amber-950/40 border-amber-500/60 text-white shadow-quantum-glow'
                  : 'bg-black/40 border-white/10 text-slate-400 hover:border-white/20'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                    l.isOn ? 'bg-amber-400/20 text-amber-300' : 'bg-white/5 text-slate-500'
                  }`}
                >
                  <Lightbulb className="w-4 h-4" />
                </div>
                <span
                  className={`text-[9px] font-mono font-bold px-2 py-0.5 rounded-full ${
                    l.isOn
                      ? 'bg-amber-400/20 text-amber-300 border border-amber-400/30'
                      : 'bg-white/5 text-slate-500'
                  }`}
                >
                  {l.isOn ? 'ON' : 'OFF'}
                </span>
              </div>
              <div className="text-xs font-semibold text-white truncate">{l.name}</div>
              <div className="text-[10px] text-slate-400 mt-0.5 truncate">{l.room}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
